import { LanguageTab } from './LanguageTab';
import type { SupportedLanguage } from '../types';
import { t } from '../utils/translations';

export type TabType = 'chat' | 'files' | 'you';

interface TabBarProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  badges: {
    files: number;
    youAction: boolean;
  };
  // Feature flags to hide tabs
  hideFilesTab?: boolean;
  hideYouTab?: boolean;
  // Language props
  supportedLanguages?: SupportedLanguage[];
  currentLanguage?: string;
  onSelectLanguage?: (code: string) => void;
  // CF suggestion props
  cfLanguage?: string | null;
  showCfSuggestion?: boolean;
  onDismissLanguageSuggestion?: () => void;
}

export function TabBar({
  activeTab,
  onTabChange,
  badges,
  hideFilesTab = false,
  hideYouTab = false,
  supportedLanguages = [],
  currentLanguage = 'en',
  onSelectLanguage = () => {},
  cfLanguage,
  showCfSuggestion = false,
  onDismissLanguageSuggestion,
}: TabBarProps) {
  // Build tabs array based on feature flags with localized labels
  const allTabs: { id: TabType; label: string }[] = [
    { id: 'chat', label: t('tab.chat', currentLanguage) },
    { id: 'files', label: t('tab.files', currentLanguage) },
    { id: 'you', label: t('tab.you', currentLanguage) },
  ];

  const tabs = allTabs.filter(tab => {
    if (tab.id === 'files' && hideFilesTab) return false;
    if (tab.id === 'you' && hideYouTab) return false;
    return true;
  });

  const showLanguageTab = supportedLanguages.length > 0;

  return (
    <div className="h-12 border-b border-gray-200 flex items-center relative z-10">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const showFilesCount = tab.id === 'files' && badges.files > 0;
        const showYouDot = tab.id === 'you' && badges.youAction && !isActive;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`
              flex-1 h-full flex items-center justify-center gap-2
              border-b-2 transition-all duration-200
              ${isActive
                ? 'border-blue-600 text-blue-600 active'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }
            `}
          >
            <span className="text-sm font-medium">
              {showFilesCount ? `${tab.label} (${badges.files})` : tab.label}
            </span>
            {showYouDot && (
              <span className="inline-block w-2 h-2 bg-red-500 rounded-full blink-dot" />
            )}
          </button>
        );
      })}

      {/* Language Tab - only if languages configured */}
      {showLanguageTab && (
        <LanguageTab
          supportedLanguages={supportedLanguages}
          currentLanguage={currentLanguage}
          onSelectLanguage={onSelectLanguage}
          cfLanguage={cfLanguage}
          showCfSuggestion={showCfSuggestion}
          onDismissSuggestion={onDismissLanguageSuggestion}
        />
      )}
    </div>
  );
}
